import { createApi } from "@reduxjs/toolkit/query/react";
import { DynamicBaseQuery } from "./baseQuery";
import { GetFormsRequest } from "./types/forms/requests/GetFormsRequest";
import { AssessmentFormsIndexResponse } from "./types/forms/responses/AssessmentFormsIndexResponse";
import {
  SubmitFormRequest,
  UpdateFormRequest,
} from "./types/forms/requests/SubmitFormRequest";
import { FormHistoryReqeust } from "./types/forms/requests/FormHistoryReqeust";
import { FormHistoryResponse } from "./types/forms/responses/FormHistoryResponse";
import { SingleFormResponse } from "./types/forms/responses/SingleFormResponse";
import { shiftHandoverApi } from "./shiftHandoverApi";
import { QuestionTypeEnum } from "./types/forms/enums/QuestionTypeEnum";
import { FormHistorySingleResponse } from "./types/forms/responses/FormHistorySingleResponse";
import { SuccessResponse } from "./types/common/responses/SuccessResponse";
import { FormPostSubmissionNoteModel } from "./types/forms/models/FormPostSubmissionNoteModel";
import { PendingSignOffsResponse } from "./types/medications/responses/PendingSignOffsResponse";
import { PendingSignOffsRequest } from "./types/medications/requests/PendingSignOffsRequest";

export const formsApi = createApi({
  reducerPath: "formsApi",
  baseQuery: DynamicBaseQuery,
  tagTypes: ["Form", "FormEntries"],
  endpoints: (builder) => ({
    getFormIndex: builder.query<AssessmentFormsIndexResponse, GetFormsRequest>({
      query: (params?) => ({
        url: "/forms",
        method: "GET",
        params,
      }),
      providesTags: (result) => {
        return result?.map((item) => ({ type: "Form", id: item.id })) ?? [];
      },
    }),
    getSingleForm: builder.query<SingleFormResponse, { formId: number }>({
      query: (params) => ({
        url: `/forms/${params.formId}`,
        method: "GET",
      }),
    }),
    submitForm: builder.mutation<{ entry_id: number }, SubmitFormRequest>({
      query: ({ residentId, formId, body }) => ({
        url: `/residents/${residentId}/fill-form/${formId}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["FormEntries"],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => {});
        dispatch(shiftHandoverApi.util.invalidateTags(["ShiftHandover"]));
      },
    }),
    submitFormNewVersion: builder.mutation<
      { entry_id: number },
      UpdateFormRequest
    >({
      query: ({ entryId, body }) => ({
        url: `/form-entries/${entryId}/new-version`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["FormEntries"],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => {});
        dispatch(shiftHandoverApi.util.invalidateTags(["ShiftHandover"]));
      },
    }),
    updateForm: builder.mutation<{ entry_id: number }, UpdateFormRequest>({
      query: ({ entryId, body }) => ({
        url: `/update-form-entry/${entryId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, args) => {
        return [{ type: "FormEntries" as const, id: args.entryId }];
      },
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => {});
        dispatch(shiftHandoverApi.util.invalidateTags(["ShiftHandover"]));
      },
    }),
    uploadFormEntryFile: builder.mutation<
      void,
      {
        entryId: number;
        question: number;
        question_type: QuestionTypeEnum;
        file: File;
      }
    >({
      query: ({ entryId, question, question_type, file }) => {
        const formData = new FormData();
        formData.append("media[]", file);
        formData.append("question_type", question_type);
        return {
          url: `/form-entries/${entryId}/questions/${question}/media`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, args) => {
        return [{ type: "FormEntries" as const, id: args.entryId }];
      },
    }),
    formHistory: builder.query<FormHistoryResponse, FormHistoryReqeust>({
      query: (params) => ({
        url: `/form-entries`,
        method: "GET",
        params,
      }),
      providesTags: (result, error, args) => {
        return [
          { type: "FormEntries" as const },
          ...(result?.data.map((item) => ({
            type: "FormEntries" as const,
            id: `${args.filter?.resident_id ?? "all"}-${args.filter?.form_tag ?? "tags"}-${args.filter?.form_id ?? "multi"}-${item.id}`,
          })) ?? []),
          ...(result?.data.map((item) => ({
            type: "FormEntries" as const,
            id: item.id,
          })) ?? []),
        ];
      },
    }),
    singleFormHistory: builder.query<
      FormHistorySingleResponse,
      { formEntryId: number }
    >({
      query: (params) => ({
        url: `/form-entries/${params.formEntryId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, args) => {
        return [{ type: "FormEntries" as const, id: args.formEntryId }];
      },
    }),
    signFormEntry: builder.mutation<SuccessResponse, { entryId: number }>({
      query: ({ entryId }) => ({
        url: `/sign-form-entry/${entryId}`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, args) => {
        return [{ type: "FormEntries" as const, id: args.entryId }];
      },
    }),
    exportFormEntryToPdf: builder.mutation<Blob, { entryId: number }>({
      query: ({ entryId }) => ({
        url: `/export-form-entry/${entryId}`,
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
    }),
    addFormPostSubmissionNote: builder.mutation<
      SuccessResponse,
      FormPostSubmissionNoteModel
    >({
      query: (body) => ({
        url: `/form-entries/post-submission-note`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { entry_id }) => [
        { type: "FormEntries", id: entry_id },
        "FormEntries",
      ],
    }),
    getAdmissionChecklistPendingSignOffs: builder.query<
      PendingSignOffsResponse,
      PendingSignOffsRequest
    >({
      query: (params) => ({
        url: `/residents/pending-sign-offs/checklists`,
        method: "GET",
        params,
      }),
    }),
    getAccidentIncidentPendingSignOffs: builder.query<
      PendingSignOffsResponse,
      PendingSignOffsRequest
    >({
      query: (params) => ({
        url: `/residents/pending-sign-offs/accidents-incidents`,
        method: "GET",
        params,
      }),
    }),
    voidFormEntry: builder.mutation<SuccessResponse, { entryId: number }>({
      query: ({ entryId }) => ({
        url: `/form-entries/${entryId}/void`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { entryId }) => [
        { type: "FormEntries", id: entryId },
        "FormEntries",
      ],
    }),
  }),
});
export const {
  useGetFormIndexQuery,
  useLazyGetFormIndexQuery,
  useGetSingleFormQuery,
  useLazyGetSingleFormQuery,
  useSubmitFormMutation,
  useSubmitFormNewVersionMutation,
  useUpdateFormMutation,
  useFormHistoryQuery,
  useLazyFormHistoryQuery,
  useSingleFormHistoryQuery,
  useUploadFormEntryFileMutation,
  usePrefetch,
  useSignFormEntryMutation,
  useExportFormEntryToPdfMutation,
  useAddFormPostSubmissionNoteMutation,
  useGetAdmissionChecklistPendingSignOffsQuery,
  useGetAccidentIncidentPendingSignOffsQuery,
  useVoidFormEntryMutation,
} = formsApi;
